import { Box, Button, Grid, MenuItem, Stack, Typography } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CustomSelect from "../../../../../components/common/Select";
import PropTypes from "prop-types";

type MappingFieldKey = "email" | "first_name" | "last_name" | "phone_number";

interface ContactsMappingKeysProps {
  readContactsFile: () => Promise<void>;
  onBack: () => void;
  headingsFromFile: string[];
  fieldsMapped: Record<MappingFieldKey, string>;
  updateFieldsMapped: (name: MappingFieldKey, value: string) => void;
}

type CustomSelectChangeEvent =
  | React.ChangeEvent<HTMLInputElement>
  | (Event & { target: { value: unknown; name: string } });

export const ContactsMappingKeys: React.FC<ContactsMappingKeysProps> = ({
  headingsFromFile,
  fieldsMapped,
  updateFieldsMapped,
  onBack,
  readContactsFile,
}) => {
  const handleSelectChange = (event: CustomSelectChangeEvent) => {
    const key = event.target.name as MappingFieldKey;
    updateFieldsMapped(key, String(event.target.value));
  };

  const canProceed =
    !!fieldsMapped.first_name &&
    !!fieldsMapped.last_name &&
    (!!fieldsMapped.email || !!fieldsMapped.phone_number);

  return (
    <Stack sx={{ width: "100%", padding: "1.3rem" }}>
      <Grid container spacing={2}>
        <Grid size={4} spacing={2}>
          <MappingAttribute name="First Name" />
          <MappingAttribute name="Last Name" />
          <MappingAttribute name="Email" />
          <MappingAttribute name="Phone Number" />
        </Grid>
        <Grid size={4}>
          <MappingArrow />
          <MappingArrow />
          <MappingArrow />
          <MappingArrow />
        </Grid>
        <Grid size={4}>
          <CustomSelect
            label={"Map First Name"}
            sx={{ width: "130px", mb: ".8rem" }}
            onChange={handleSelectChange}
            name="first_name"
            size="small"
            value={fieldsMapped.first_name}
          >
            {headingsFromFile.map((heading, index) => {
              return (
                <MenuItem key={index} value={heading}>
                  {heading}
                </MenuItem>
              );
            })}
          </CustomSelect>
          <CustomSelect
            label={"Map Last Name"}
            name="last_name"
            onChange={handleSelectChange}
            value={fieldsMapped.last_name}
            sx={{ width: "130px", mb: ".8rem" }}
            size="small"
          >
            {headingsFromFile.map((heading, index) => {
              return (
                <MenuItem key={index} value={heading}>
                  {heading}
                </MenuItem>
              );
            })}
          </CustomSelect>
          <CustomSelect
            value={fieldsMapped.email}
            onChange={handleSelectChange}
            name="email"
            label={"Map Email"}
            sx={{ width: "130px", mb: ".8rem" }}
            size="small"
          >
            {headingsFromFile.map((heading, index) => {
              return (
                <MenuItem key={index} value={heading}>
                  {heading}
                </MenuItem>
              );
            })}
          </CustomSelect>
          <CustomSelect
            label={"Map Phone Number"}
            onChange={handleSelectChange}
            name={"phone_number"}
            value={fieldsMapped.phone_number}
            sx={{ width: "130px", mb: ".8rem" }}
            size="small"
          >
            {headingsFromFile.map((heading, index) => {
              return (
                <MenuItem key={index} value={heading}>
                  {heading}
                </MenuItem>
              );
            })}
          </CustomSelect>
        </Grid>
      </Grid>

      <Box
        sx={{
          mt: "1rem",
          padding: ".5em .6em",
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
        }}
      >
        <Button variant="outlined" onClick={onBack}>
          Back
        </Button>
        <Button
          variant="contained"
          onClick={readContactsFile}
          disabled={!canProceed}
        >
          Proceed
        </Button>
      </Box>
    </Stack>
  );
};

ContactsMappingKeys.propTypes = {
  readContactsFile: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  headingsFromFile: PropTypes.arrayOf(PropTypes.string).isRequired,
  fieldsMapped: PropTypes.object.isRequired,
  updateFieldsMapped: PropTypes.func.isRequired,
};

const MappingAttribute: React.FC<{ name: string }> = ({ name }) => {
  return (
    <Box
      sx={{
        padding: ".6rem",
        borderRadius: "0.5rem",
        border: `1px solid #CBD5E1`,
        mb: ".5rem",
      }}
    >
      <Typography variant="body2">{name}</Typography>
    </Box>
  );
};

MappingAttribute.propTypes = {
  name: PropTypes.string.isRequired,
};

const MappingArrow: React.FC = () => {
  return (
    <Box
      sx={{
        padding: ".6rem",
        borderRadius: "0.5rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        mb: ".5rem",
      }}
    >
      <ArrowForwardIcon sx={{ width: "100%" }} />
    </Box>
  );
};